import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import dlLogo from '../assets/dl-logo.png';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  // Keyboard: Escape closes dropdown
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsServicesOpen(false);
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const services = [
    { name: 'Sea Freight', path: '/services/sea-freight' },
    { name: 'Air Freight', path: '/services/air-freight' },
    { name: 'Land Freight', path: '/services/land-freight' },
    { name: 'Project Logistics', path: '/services/project-logistics' },
    { name: 'Customs Clearance', path: '/services/customs-clearance' },
    { name: 'Warehousing & Value-Added Services', path: '/services/warehousing' },
  ];

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT US', path: '/about' },
    { name: 'SERVICES', path: '/services', dropdown: services },
    { name: 'INDUSTRIES', path: '/industries' },
    { name: 'GLOBAL NETWORK', path: '/global-network' },
    { name: 'GET IN TOUCH', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="header__container container">
        <Link to="/" className="header__logo" aria-label="Denver Logistics Home">
          <img src={dlLogo} alt="Denver Logistics" className="header__logo-img" />
        </Link>

        <nav
          className={`header__nav ${isMenuOpen ? 'header__nav--open' : ''}`}
          aria-label="Main navigation"
        >
          <ul className="header__nav-list" role="menubar">
            {navLinks.map((link) => (
              <li
                key={link.name}
                className={`header__nav-item ${link.dropdown ? 'header__nav-item--dropdown' : ''}`}
                onMouseEnter={() => link.dropdown && setIsServicesOpen(true)}
                onMouseLeave={() => link.dropdown && setIsServicesOpen(false)}
                role="none"
              >
                {link.dropdown ? (
                  <>
                    <button
                      className={`header__nav-link ${isActive('/services') ? 'header__nav-link--active' : ''}`}
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      aria-expanded={isServicesOpen}
                      aria-haspopup="true"
                      role="menuitem"
                    >
                      {link.name}
                      <ChevronDown size={14} className={`header__chevron ${isServicesOpen ? 'header__chevron--open' : ''}`} />
                    </button>
                    <ul
                      className={`header__dropdown ${isServicesOpen ? 'header__dropdown--open' : ''}`}
                      role="menu"
                      aria-label="Services submenu"
                    >
                      {link.dropdown.map((item) => (
                        <li key={item.name} role="none">
                          <Link to={item.path} className="header__dropdown-link" role="menuitem">
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`header__nav-link ${isActive(link.path) ? 'header__nav-link--active' : ''}`}
                    role="menuitem"
                  >
                    {link.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {/* Mobile-only CTA — only renders inside mobile nav */}
          <Link to="/quote" className="btn btn-primary header__cta-mobile">
            GET A QUOTE &rarr;
          </Link>
        </nav>

        {/* Desktop-only CTA */}
        <Link to="/quote" className="btn btn-primary btn-sm header__cta">
          GET A QUOTE &rarr;
        </Link>

        <button
          className="header__toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>
  );
};

export default Header;
